import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check, X, AlertTriangle, Search, Link as LinkIcon } from "lucide-react";
import { Save } from "lucide-react";
import { toast } from "sonner";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

/**
 * VademecumTechEdit
 * Editor del bloque "Información técnica" del producto base.
 * Soporta autocomplete con validación suave de duplicados.
 *
 * Props:
 *  - item: producto_base (objeto del Vademécum)
 *  - opciones: { grupos_terapeuticos, subgrupos, categorias, especialidades, vias_administracion, patologias }
 *  - productosComerciales: array de PCs para linkear combos/competencia
 *  - onSaved: callback
 */
export function VademecumTechEdit({ item, opciones, productosComerciales = [], onSaved }) {
  const [form, setForm] = useState(initialForm(item));
  const [saving, setSaving] = useState(false);

  function initialForm(it) {
    return {
      grupo_terapeutico: it?.grupo_terapeutico || "",
      subgrupo: it?.subgrupo || "",
      especialidades: it?.especialidades || [],
      mecanismo_accion: it?.mecanismo_accion || "",
      vias_administracion: it?.vias_administracion || [],
      contraindicaciones: it?.contraindicaciones || "",
      advertencias: it?.advertencias || "",
      patologias: it?.patologias || [],
      posologia: it?.posologia || "",
      interacciones_criticas: it?.interacciones_criticas || "",
      temporada_demanda: it?.temporada_demanda || [],
      combos: it?.combos || [],
      competencia: it?.competencia || [],
    };
  }

  useEffect(() => { setForm(initialForm(item)); /* eslint-disable-next-line */ }, [item?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/vademecum/producto-base/${item.id}`, form);
      toast.success("Información técnica guardada");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tech-edit-form">
      {/* === Clasificación === */}
      <SectionTitle>Clasificación terapéutica</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FieldAutocomplete
          label="Grupo Terapéutico (Nivel 1)"
          placeholder="Ej. Sistema Cardiovascular"
          value={form.grupo_terapeutico}
          onChange={(v) => setForm(f => ({ ...f, grupo_terapeutico: v }))}
          opciones={opciones?.grupos_terapeuticos || []}
          campo="grupo_terapeutico"
          testId="tech-grupo"
        />
        <FieldAutocomplete
          label="Subgrupo (Nivel 2)"
          placeholder="Ej. Antihipertensivos"
          value={form.subgrupo}
          onChange={(v) => setForm(f => ({ ...f, subgrupo: v }))}
          opciones={opciones?.subgrupos || []}
          campo="subgrupo"
          testId="tech-subgrupo"
        />
      </div>

      {/* === Especialidades === */}
      <SectionTitle>Médicos que lo recetan</SectionTitle>
      <TagsInput
        label="Especialidades médicas"
        placeholder="Cardiólogo, Internista, Médico general…"
        value={form.especialidades}
        onChange={(v) => setForm(f => ({ ...f, especialidades: v }))}
        opciones={opciones?.especialidades || []}
        campo="especialidades"
        testId="tech-especialidades"
      />

      {/* === Mecanismo y Vías === */}
      <SectionTitle>Mecanismo y administración</SectionTitle>
      <div>
        <Label className="text-xs uppercase font-semibold text-[#5c7368]">Mecanismo de acción (en simple)</Label>
        <Textarea
          rows={2}
          value={form.mecanismo_accion}
          onChange={(e) => setForm(f => ({ ...f, mecanismo_accion: e.target.value }))}
          placeholder='Ej. "Bloquea el receptor del dolor"'
          data-testid="tech-mecanismo"
        />
      </div>
      <TagsInput
        label="Vías de administración"
        placeholder="Tabletas, Ampollas, Cremas, Jarabe…"
        value={form.vias_administracion}
        onChange={(v) => setForm(f => ({ ...f, vias_administracion: v }))}
        opciones={opciones?.vias_administracion || []}
        campo="vias_administracion"
        testId="tech-vias"
      />

      {/* === Uso clínico === */}
      <SectionTitle>Uso clínico</SectionTitle>
      <TagsInput
        label="Patologías comunes"
        placeholder="Hipertensión, Migraña, Diabetes…"
        value={form.patologias}
        onChange={(v) => setForm(f => ({ ...f, patologias: v }))}
        opciones={opciones?.patologias || []}
        campo="patologias"
        testId="tech-patologias"
      />
      <div>
        <Label className="text-xs uppercase font-semibold text-[#5c7368]">Posología simplificada</Label>
        <Textarea
          rows={2}
          value={form.posologia}
          onChange={(e) => setForm(f => ({ ...f, posologia: e.target.value }))}
          placeholder='Ej. "Una tableta al día por 5 días" o "1 ampolla cada 8 horas"'
          data-testid="tech-posologia"
        />
      </div>

      {/* === Seguridad === */}
      <SectionTitle tone="warn">Seguridad y advertencias</SectionTitle>
      <div>
        <Label className="text-xs uppercase font-semibold text-[#5c7368]">Contraindicaciones absolutas</Label>
        <Textarea
          rows={3}
          value={form.contraindicaciones}
          onChange={(e) => setForm(f => ({ ...f, contraindicaciones: e.target.value }))}
          placeholder="Ej. Embarazo, insuficiencia renal severa, hipersensibilidad conocida…"
          data-testid="tech-contraind"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-semibold text-[#5c7368]">Advertencias de uso</Label>
        <Textarea
          rows={2}
          value={form.advertencias}
          onChange={(e) => setForm(f => ({ ...f, advertencias: e.target.value }))}
          placeholder="Ej. Produce somnolencia, no mezclar con alcohol, evitar manejar maquinaria pesada…"
          data-testid="tech-advert"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-semibold text-[#5c7368]">Interacciones críticas</Label>
        <Textarea
          rows={3}
          value={form.interacciones_criticas}
          onChange={(e) => setForm(f => ({ ...f, interacciones_criticas: e.target.value }))}
          placeholder="Ej. Warfarina (aumenta sangrado), antibióticos macrólidos, jugo de toronja…"
          data-testid="tech-inter"
        />
      </div>

      {/* === Comercial / venta === */}
      <SectionTitle>Estrategia comercial</SectionTitle>
      <div>
        <Label className="text-xs uppercase font-semibold text-[#5c7368] mb-1">Temporada de alta demanda</Label>
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid="tech-temporada">
          {MESES.map((m, idx) => {
            const mes = idx + 1;
            const sel = form.temporada_demanda.includes(mes);
            return (
              <button
                key={mes}
                type="button"
                onClick={() => setForm(f => ({
                  ...f,
                  temporada_demanda: sel ? f.temporada_demanda.filter(x => x !== mes) : [...f.temporada_demanda, mes].sort((a,b) => a-b)
                }))}
                className={`text-xs px-2.5 py-1 rounded-md border-2 font-semibold ${sel ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6] hover:border-[#205c45]"}`}
                data-testid={`mes-${mes}`}
              >
                {m}
              </button>
            );
          })}
          <Button size="sm" variant="ghost" onClick={() => setForm(f => ({...f, temporada_demanda: []}))} className="h-8 text-xs">
            Todo el año
          </Button>
        </div>
        <p className="text-[10px] text-[#5c7368] mt-1">Si no marca ningún mes, se considera "Todo el año" (sin estacionalidad).</p>
      </div>

      <ComboPicker
        label="Sugerencia de combo / Venta cruzada"
        placeholder="Ej. Protector gástrico"
        value={form.combos}
        onChange={(v) => setForm(f => ({ ...f, combos: v }))}
        productos={productosComerciales}
        testIdBase="tech-combo"
      />
      <ComboPicker
        label="Competencia directa"
        placeholder="Ej. Marca rival similar"
        value={form.competencia}
        onChange={(v) => setForm(f => ({ ...f, competencia: v }))}
        productos={productosComerciales}
        testIdBase="tech-competencia"
      />

      <div className="flex justify-end pt-3 border-t border-[#e9f2ee]">
        <Button onClick={save} disabled={saving} className="bg-[#205c45]" data-testid="tech-save-btn">
          <Save size={14} className="mr-1.5" /> {saving ? "Guardando…" : "Guardar información técnica"}
        </Button>
      </div>
    </div>
  );
}

function SectionTitle({ children, tone = "default" }) {
  const cls = tone === "warn"
    ? "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]"
    : "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]";
  return (
    <div className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded border ${cls} inline-block`}>
      {children}
    </div>
  );
}

/** Campo de texto con autocomplete + validación suave de duplicados. */
function FieldAutocomplete({ label, placeholder, value, onChange, opciones, campo, testId }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showDup, setShowDup] = useState(null);

  const checkDup = async (v) => {
    if (!v || !v.trim() || !campo) { setShowDup(null); return; }
    if (opciones.some(o => o.trim().toLowerCase() === v.trim().toLowerCase())) {
      setShowDup(null);
      return;
    }
    try {
      const { data } = await api.get(`/vademecum/autocomplete-dupes`, { params: { valor: v, campo } });
      setShowDup(data.matches?.[0] || null);
    } catch { setShowDup(null); }
  };

  return (
    <div>
      <Label className="text-xs uppercase font-semibold text-[#5c7368]">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open}
            className={`h-10 justify-between font-normal text-left w-full ${!value ? "text-[#8ba397]" : ""}`}
            data-testid={`${testId}-trigger`}>
            <span className="truncate">{value || placeholder}</span>
            <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(420px,calc(100vw-24px))] max-w-[420px] p-0" align="start">
          <Command shouldFilter={true} filter={(v, s) => v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0}>
            <div className="flex items-center border-b border-[#d1dbd6] px-3">
              <Search size={14} className="mr-2 shrink-0 opacity-50" />
              <CommandInput
                placeholder="Escribir o seleccionar…"
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none"
                value={value}
                onValueChange={(v) => { onChange(v); checkDup(v); setSuggestions(opciones.filter(o => o.toLowerCase().includes(v.toLowerCase()))); }}
                data-testid={`${testId}-input`}
              />
            </div>
            <CommandList className="max-h-[260px]">
              <CommandEmpty className="py-3 text-center text-xs text-[#8ba397]">
                Sin coincidencias. Escriba para crear nuevo valor.
              </CommandEmpty>
              <CommandGroup>
                {(suggestions.length > 0 ? suggestions : opciones).map(o => (
                  <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); setShowDup(null); }}>
                    <Check size={14} className={`mr-2 ${value === o ? "opacity-100 text-[#205c45]" : "opacity-0"}`} />
                    {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showDup && (
        <div className="mt-1 text-[11px] flex items-center gap-2 bg-[#fef7ec] text-[#8a5500] border border-[#f5c477] rounded px-2 py-1" data-testid={`${testId}-dup`}>
          <AlertTriangle size={12} />
          <span>¿Quisiste decir "<strong>{showDup.valor}</strong>"? ({Math.round(showDup.ratio*100)}% similar)</span>
          <button type="button" onClick={() => { onChange(showDup.valor); setShowDup(null); }} className="ml-1 underline font-semibold">Usar este</button>
        </div>
      )}
    </div>
  );
}

/** Input de tags (chips) con autocomplete contra opciones y detección de duplicado. */
function TagsInput({ label, placeholder, value, onChange, opciones, campo, testId }) {
  const [text, setText] = useState("");
  const [showDup, setShowDup] = useState(null);

  const add = async (v) => {
    const n = (v || text).trim();
    if (!n) return;
    if (value.some(x => x.toLowerCase() === n.toLowerCase())) return;  // ya está
    // Verificar similitud
    if (!opciones.some(o => o.toLowerCase() === n.toLowerCase())) {
      try {
        const { data } = await api.get(`/vademecum/autocomplete-dupes`, { params: { valor: n, campo } });
        if (data.matches?.length > 0) {
          setShowDup({ original: n, ...data.matches[0] });
          return;
        }
      } catch { /* noop */ }
    }
    onChange([...value, n]);
    setText("");
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const acceptDup = (use) => {
    if (use === "original") onChange([...value, showDup.original]);
    else onChange([...value, showDup.valor]);
    setShowDup(null);
    setText("");
  };

  const filtradas = useMemo(() => opciones.filter(o =>
    o.toLowerCase().includes(text.toLowerCase()) && !value.some(v => v.toLowerCase() === o.toLowerCase())
  ).slice(0, 6), [text, opciones, value]);

  return (
    <div>
      <Label className="text-xs uppercase font-semibold text-[#5c7368]">{label}</Label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1 mb-1.5">
          {value.map((tag, i) => (
            <Badge key={i} className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] flex items-center gap-1" data-testid={`${testId}-tag-${i}`}>
              {tag}
              <button type="button" onClick={() => remove(i)} className="hover:text-[#b53a25]"><X size={11} /></button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
        />
        <Button type="button" onClick={() => add()} variant="outline" size="sm" data-testid={`${testId}-add-btn`}>Agregar</Button>
      </div>
      {filtradas.length > 0 && text && (
        <div className="mt-1 flex flex-wrap gap-1">
          {filtradas.map(o => (
            <button key={o} type="button" onClick={() => add(o)} className="text-[10px] px-2 py-0.5 rounded border bg-white text-[#5c7368] border-[#d1dbd6] hover:bg-[#e9f2ee] hover:text-[#205c45]">
              + {o}
            </button>
          ))}
        </div>
      )}
      {showDup && (
        <div className="mt-2 text-[11px] bg-[#fef7ec] text-[#8a5500] border border-[#f5c477] rounded p-2" data-testid={`${testId}-dup`}>
          <div className="flex items-center gap-1 mb-1"><AlertTriangle size={12} /><strong>Posible duplicado</strong></div>
          <div>Escribiste "<strong>{showDup.original}</strong>" pero ya existe "<strong>{showDup.valor}</strong>" ({Math.round(showDup.ratio*100)}% similar).</div>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => acceptDup("existing")} className="underline font-semibold">Usar "{showDup.valor}"</button>
            <button type="button" onClick={() => acceptDup("original")} className="underline">Mantener mi texto</button>
            <button type="button" onClick={() => setShowDup(null)} className="ml-auto opacity-60">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Editor de lista de combos / competencia: texto libre + opcional link a PC. */
function ComboPicker({ label, placeholder, value, onChange, productos, testIdBase }) {
  const [text, setText] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkedPc, setLinkedPc] = useState(null);

  const add = () => {
    const n = text.trim();
    if (!n && !linkedPc) return;
    onChange([...value, {
      texto: n || (linkedPc?.nombre_comercial || ""),
      producto_comercial_id: linkedPc?.id || null,
    }]);
    setText("");
    setLinkedPc(null);
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div>
      <Label className="text-xs uppercase font-semibold text-[#5c7368]">{label}</Label>
      {value.length > 0 && (
        <div className="space-y-1 mt-1 mb-2">
          {value.map((it, i) => (
            <div key={i} className="flex items-center gap-2 bg-white border border-[#d1dbd6] rounded px-2 py-1 text-sm" data-testid={`${testIdBase}-item-${i}`}>
              <span className="flex-1">{it.texto}</span>
              {it.producto_comercial_id && (
                <Badge variant="outline" className="text-[10px] text-[#205c45] border-[#b2d0c2]">
                  <LinkIcon size={9} className="mr-0.5" /> linkeado
                </Badge>
              )}
              <button type="button" onClick={() => remove(i)} className="hover:text-[#b53a25]"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} data-testid={`${testIdBase}-input`} />
        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" data-testid={`${testIdBase}-link-btn`}>
              <LinkIcon size={12} className="mr-1" /> {linkedPc ? linkedPc.nombre_comercial.slice(0,16) : "Linkear"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(380px,calc(100vw-24px))] p-0" align="end">
            <Command shouldFilter={true} filter={(v, s) => v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0}>
              <CommandInput placeholder="Buscar producto comercial…" className="h-9" />
              <CommandList className="max-h-[260px]">
                <CommandEmpty className="py-3 text-center text-xs text-[#8ba397]">Sin coincidencias</CommandEmpty>
                <CommandGroup>
                  {productos.map(p => (
                    <CommandItem key={p.id} value={`${p.nombre_comercial} ${p.codigo_interno || ""}`}
                      onSelect={() => { setLinkedPc(p); setText(p.nombre_comercial); setLinkOpen(false); }}>
                      <div className="flex flex-col">
                        <span className="font-medium">{p.nombre_comercial}</span>
                        <span className="text-[10px] text-[#5c7368]">{p.codigo_interno} · {p.laboratorio_nombre}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button type="button" onClick={add} variant="outline" size="sm" data-testid={`${testIdBase}-add-btn`}>Agregar</Button>
      </div>
      <p className="text-[10px] text-[#5c7368] mt-1">Texto libre + linkear opcionalmente a un producto del sistema (chip "linkeado").</p>
    </div>
  );
}

/** Vista de solo lectura para mostrar la información técnica dentro de la fila expandida. */
export function VademecumTechView({ item }) {
  const has = (v) => v != null && v !== "" && (!Array.isArray(v) || v.length > 0);
  if (!has(item.grupo_terapeutico) && !has(item.subgrupo) && !has(item.especialidades) && !has(item.mecanismo_accion) && !has(item.patologias) && !has(item.posologia) && !has(item.contraindicaciones) && !has(item.advertencias) && !has(item.interacciones_criticas) && !has(item.combos) && !has(item.competencia) && !has(item.temporada_demanda)) {
    return (
      <div className="text-xs text-[#8ba397] italic py-2">
        Sin información técnica. Use "Editar" para completar.
      </div>
    );
  }
  return (
    <div className="space-y-3 text-sm" data-testid="tech-view">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {has(item.grupo_terapeutico) && <InfoBlock label="Grupo terapéutico">{item.grupo_terapeutico}</InfoBlock>}
        {has(item.subgrupo) && <InfoBlock label="Subgrupo">{item.subgrupo}</InfoBlock>}
      </div>
      {has(item.especialidades) && (
        <InfoBlock label="Médicos que lo recetan">
          <ChipList items={item.especialidades} tone="green" />
        </InfoBlock>
      )}
      {has(item.mecanismo_accion) && <InfoBlock label="Mecanismo de acción">{item.mecanismo_accion}</InfoBlock>}
      {has(item.vias_administracion) && (
        <InfoBlock label="Vías de administración">
          <ChipList items={item.vias_administracion} tone="blue" />
        </InfoBlock>
      )}
      {has(item.patologias) && (
        <InfoBlock label="Patologías comunes">
          <ChipList items={item.patologias} tone="default" />
        </InfoBlock>
      )}
      {has(item.posologia) && <InfoBlock label="Posología">{item.posologia}</InfoBlock>}
      {has(item.contraindicaciones) && <InfoBlock label="Contraindicaciones" tone="warn">{item.contraindicaciones}</InfoBlock>}
      {has(item.advertencias) && <InfoBlock label="Advertencias" tone="warn">{item.advertencias}</InfoBlock>}
      {has(item.interacciones_criticas) && <InfoBlock label="Interacciones críticas" tone="warn">{item.interacciones_criticas}</InfoBlock>}
      {has(item.temporada_demanda) && (
        <InfoBlock label="Temporada de alta demanda">
          <div className="flex flex-wrap gap-1">
            {item.temporada_demanda.map(m => (
              <Badge key={m} className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">{MESES[m-1]}</Badge>
            ))}
          </div>
        </InfoBlock>
      )}
      {has(item.combos) && (
        <InfoBlock label="Combo / Venta cruzada">
          <ComboList items={item.combos} />
        </InfoBlock>
      )}
      {has(item.competencia) && (
        <InfoBlock label="Competencia directa">
          <ComboList items={item.competencia} />
        </InfoBlock>
      )}
    </div>
  );
}

function InfoBlock({ label, children, tone = "default" }) {
  const cls = tone === "warn"
    ? "bg-[#fef7ec] border-[#f5c477]"
    : "bg-white border-[#d1dbd6]";
  return (
    <div className={`rounded border ${cls} p-2.5`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#5c7368] mb-1">{label}</div>
      <div className="text-[13px] text-[#15241d] whitespace-pre-line">{children}</div>
    </div>
  );
}

function ChipList({ items, tone = "default" }) {
  const colors = {
    default: "bg-white text-[#5c7368] border-[#d1dbd6]",
    green: "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]",
    blue: "bg-[#eaf4fb] text-[#2d4a73] border-[#9cc6e3]",
  };
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((i, idx) => (
        <Badge key={idx} variant="outline" className={colors[tone]}>{i}</Badge>
      ))}
    </div>
  );
}

function ComboList({ items }) {
  return (
    <ul className="space-y-1">
      {items.map((it, idx) => (
        <li key={idx} className="flex items-center gap-2 text-[13px]">
          <span>•</span>
          <span>{it.texto}</span>
          {it.producto_comercial_id && (
            <Badge variant="outline" className="text-[10px] text-[#205c45] border-[#b2d0c2]">
              <LinkIcon size={9} className="mr-0.5" /> Producto en sistema
            </Badge>
          )}
        </li>
      ))}
    </ul>
  );
}
